import { useState, useEffect, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import initialData from "./data.json";
import "./home.css";

/* ─────────────────────────────────────────
   Constants & Hooks
 ───────────────────────────────────────── */
const BAR_HEIGHTS = [6, 10, 8, 14, 12, 18, 14, 20, 16, 24, 20, 28, 24, 20, 16, 22, 18, 24, 20, 16, 14, 18, 12, 16];

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const check = () => setIsMobile(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);
    return isMobile;
}

function useScrollY() {
    const [y, setY] = useState(0);
    useEffect(() => {
        const h = () => setY(window.scrollY);
        window.addEventListener("scroll", h, { passive: true });
        return () => window.removeEventListener("scroll", h);
    }, []);
    return y;
}

function useMousePosition() {
    const [pos, setPos] = useState({ x: 0, y: 0 });
    useEffect(() => {
        const h = (e) => setPos({ x: e.clientX, y: e.clientY });
        window.addEventListener("mousemove", h);
        return () => window.removeEventListener("mousemove", h);
    }, []);
    return pos;
}

/* ─────────────────────────────────────────
   Primitives
 ───────────────────────────────────────── */
function Glass({ children, className = "", hover = false, style = {} }) {
    return (
        <div className={`glass ${hover ? "glass--hover" : ""} ${className}`} style={style}>
            <div className="glass__shimmer" />
            {children}
        </div>
    );
}

function BackgroundStars() {
    const mouse = useMousePosition();
    const stars = useRef(
        Array.from({ length: 120 }, () => ({
            top: Math.random() * 100,
            left: Math.random() * 100,
            size: Math.random() < 0.12 ? 2 : 1,
            opacity: Math.random() * 0.45 + 0.08,
            dur: 3 + Math.random() * 5,
            delay: Math.random() * 5,
            depth: Math.random() * 0.5 + 0.2, // Movement multiplier
        }))
    ).current;

    return (
        <div className="stars-container">
            {stars.map((s, i) => {
                const offsetX = (mouse.x - window.innerWidth / 2) * s.depth * 0.02;
                const offsetY = (mouse.y - window.innerHeight / 2) * s.depth * 0.02;
                return (
                    <div
                        key={i}
                        className="star"
                        style={{
                            width: s.size, height: s.size,
                            top: `${s.top}%`, left: `${s.left}%`,
                            opacity: s.opacity,
                            transform: `translate(${offsetX}px, ${offsetY}px)`,
                            transition: 'transform 0.1s ease-out',
                            animation: `twinkle ${s.dur}s ease-in-out ${s.delay}s infinite`,
                        }}
                    />
                );
            })}
        </div>
    );
}

function Orb({ size, top, left, right, bottom, color, delay = 0, style = {} }) {
    return (
        <div className="orb" style={{ width: size, height: size, top, left, right, bottom, background: color, animationDelay: `${delay}s`, ...style }} />
    );
}

/* ─────────────────────────────────────────
   Components
 ───────────────────────────────────────── */
function Nav({ scrollY }) {
    const scrolled = scrollY > 60;
    return (
        <nav className={`nav ${scrolled ? "nav--scrolled" : ""}`}>
            <div className={`nav__inner ${scrolled ? "nav__inner--scrolled" : ""}`}>
                <a href="#root" className="nav__logo-link" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                    <img src="/favicon.svg" alt="Rakthe" className="nav__logo-icon" />
                    <span className="nav__logo-text">Rakthe</span>
                </a>
                <div className="nav__links">
                    {["about", "projects", "contact"].map((link) => (
                        <a key={link} href={`#${link}`} className="nav__link">{link}</a>
                    ))}
                </div>
            </div>
        </nav>
    );
}



function MobileNav() {
    return (
        <div className="mobile-nav">
            <div className="mobile-nav__bar">
                <a href="#about" className="mobile-nav__link" title="About">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                    <span className="mobile-nav__label">about</span>
                </a>
                <a href="#projects" className="mobile-nav__link" title="Projects">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>
                    <span className="mobile-nav__label">projects</span>
                </a>
                <a href="#contact" className="mobile-nav__link" title="Contact">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                    <span className="mobile-nav__label">contact</span>
                </a>
            </div>
        </div>
    );
}

const useTypewriter = (text, speed = 100, delay = 1000) => {
    const [displayedText, setDisplayedText] = useState("");
    const [isStarted, setIsStarted] = useState(false);
    useEffect(() => {
        const timeout = setTimeout(() => setIsStarted(true), delay);
        return () => clearTimeout(timeout);
    }, [delay, text]);
    useEffect(() => {
        setDisplayedText("");
        setIsStarted(false);
    }, [text]);
    useEffect(() => {
        if (!isStarted) return;
        if (displayedText.length < text.length) {
            const timeout = setTimeout(() => setDisplayedText(text.slice(0, displayedText.length + 1)), speed);
            return () => clearTimeout(timeout);
        }
    }, [displayedText, text, speed, isStarted]);
    return displayedText;
};

function Hero({ isMobile, data }) {
    const ref = useRef(null);
    const vis = useInView(ref);
    const mouse = useMousePosition();
    const typedName = useTypewriter(data.bio.name, 150, 500);

    return (
        <section id="hero" ref={ref} className="hero">
            {!isMobile && <motion.div className="hero__spotlight" animate={{ x: mouse.x - 400, y: mouse.y - 400 }} transition={{ type: "tween", ease: "backOut", duration: 0.5 }} />}
            <Orb size={700} top="-20%" left="-18%" color="radial-gradient(circle, rgba(25, 30, 80, 0.35) 0%, transparent 70%)" />
            <Orb size={500} bottom="-12%" right="-14%" color="radial-gradient(circle, rgba(15, 20, 60, 0.3) 0%, transparent 70%)" delay={3} />
            <div className="hero__grid" />
            <motion.div
                className="hero__bg-logo"
                animate={!isMobile ? {
                    x: (mouse.x - (typeof window !== 'undefined' ? window.innerWidth : 1000) / 2) * -0.02,
                    y: (mouse.y - (typeof window !== 'undefined' ? window.innerHeight : 800) / 2) * -0.02,
                } : {}}
                transition={{ type: "tween", ease: "easeOut", duration: 0.6 }}
                aria-hidden="true"
            >
                <div className="hero__bg-logo-inner">
                    <img src="/logo-standalone.svg" alt="" className="hero__bg-logo-img" />
                </div>
            </motion.div>
            <div className="hero__tilt-wrapper">
                <motion.div className="hero__content" initial={{ opacity: 0, y: 20 }} animate={vis ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
                    <Glass className="hero__badge"><span className="hero__badge-text"> connecting...</span></Glass>
                    <motion.h1 className="hero__name" whileHover={{ scale: 1.01, filter: "brightness(1.05)" }}>{typedName}<span className="hero__cursor">|</span></motion.h1>
                    <div className="hero__divider"><div className="hero__divider-line hero__divider-line--left" /><span className="hero__role">{data.bio.role}</span><div className="hero__divider-line hero__divider-line--right" /></div>
                    <p className="hero__tagline">{data.bio.summary.split('.')[0]}.</p>
                    <div className="hero__ctas"><a href="#projects" className="btn--primary">View work</a><a href="#contact" className="btn--ghost">Get in touch</a></div>
                    <div className="hero__scroll">
                        <div className="hero__scroll-text">scroll</div>
                        <div className="hero__scroll-line" />
                    </div>
                </motion.div>
            </div>
            <div className="hero__float hero__float--left"><Glass className="float-card"><div className="float-card__label">uptime</div><div className="float-card__bar"><div className="float-card__bar-fill" style={{ width: "99.97%" }} /></div></Glass></div>
            <div className="hero__float hero__float--right"><Glass className="float-card"><div className="float-card__label">commits</div><div className="float-card__bars">{BAR_HEIGHTS.map((h, i) => <div key={i} className={`float-card__bar-col ${i > (BAR_HEIGHTS.length - 7) ? "float-card__bar-col--active" : "float-card__bar-col--inactive"}`} style={{ height: h }} />)}</div></Glass></div>
        </section>
    );
}

function About({ data }) {
    const ref = useRef(null);
    const vis = useInView(ref);
    return (
        <section id="about" ref={ref} className="section section--about">
            <div className="section__inner">
                <div className={`fade-up ${vis ? "fade-up--visible" : ""}`}><span className="section__label">01 / about</span><h2 className="section__heading">Engineered for<br /><span className="section__heading-accent">production.</span></h2></div>
                <div className="about__grid">
                    <div className="about__left-col" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <Glass hover className={`bio fade-up fade-up--d1 ${vis ? "fade-up--visible" : ""}`}>
                            <div className="bio__header">
                                {data.bio.avatar ? (
                                    <img src={data.bio.avatar} alt={data.bio.name} className="bio__avatar-img" />
                                ) : (
                                    <div className="bio__avatar">BR</div>
                                )}
                                <div className="bio__info">
                                    <div className="bio__name">{data.bio.name}</div>
                                    <div className="bio__meta">{data.bio.role} · {data.bio.location}</div>
                                </div>
                            </div>
                            <div className="bio__divider" /><p className="bio__text">{data.bio.summary}</p>
                        </Glass>
                        <Glass hover id="experience" className={`experience-card fade-up fade-up--d1 ${vis ? "fade-up--visible" : ""}`} style={{ padding: '24px 28px' }}>
                            <div className="experience-list" style={{ marginTop: 0 }}>
                                <div className="card-label" style={{ marginBottom: 20 }}>Work Experience</div>
                                {data.experience.map((exp, i) => (
                                    <div key={i} className="exp-item">
                                        <div className="exp-item__top">
                                            <span className="exp-item__company">
                                                {exp.link ? (
                                                    <a href={exp.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }} className="project-link">
                                                        {exp.company} <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 3, display: 'inline-block', verticalAlign: 'middle', opacity: 0.7 }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" /></svg>
                                                    </a>
                                                ) : (
                                                    exp.company
                                                )}
                                            </span>
                                            <span className="exp-item__year">{exp.year}</span>
                                        </div>
                                        <div className="exp-item__role">{exp.role}</div><p className="exp-item__desc">{exp.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </Glass>
                        {data.freelance && data.freelance.length > 0 && (
                            <Glass hover id="freelance" className={`experience-card fade-up fade-up--d1 ${vis ? "fade-up--visible" : ""}`} style={{ padding: '24px 28px' }}>
                                <div className="experience-list" style={{ marginTop: 0 }}>
                                    <div className="card-label" style={{ marginBottom: 20 }}>Freelance Experience</div>
                                    {data.freelance.map((free, i) => (
                                        <div key={i} className="exp-item">
                                            <div className="exp-item__top">
                                                <span className="exp-item__company">
                                                    {free.link ? (
                                                        <a href={free.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }} className="project-link">
                                                            {free.company} <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 3, display: 'inline-block', verticalAlign: 'middle', opacity: 0.7 }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3" /></svg>
                                                        </a>
                                                    ) : (
                                                        free.company
                                                    )}
                                                </span>
                                                {free.year && <span className="exp-item__year">{free.year}</span>}
                                            </div>
                                            <div className="exp-item__role">{free.role}</div><p className="exp-item__desc">{free.desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </Glass>
                        )}
                    </div>
                    <div className="skills-panel">
                        <Glass hover className={`tags-card fade-up fade-up--d2 ${vis ? "fade-up--visible" : ""}`}>
                            <div className="card-label" style={{ marginBottom: 16 }}>also in the stack</div>
                            <div className="stack-groups">
                                {data.stack.map((group) => (
                                    <div key={group.group} className="stack-group">
                                        <div className="stack-group__name">{group.group}</div>
                                        <div className="tags-grid">{group.tags.map((tag) => <span key={tag} className="tag">{tag}</span>)}</div>
                                    </div>
                                ))}
                            </div>
                        </Glass>
                        <Glass hover className={`skills-card fade-up fade-up--d3 ${vis ? "fade-up--visible" : ""}`}>
                            <div className="card-label">education</div>
                            <div className="edu-list">
                                {data.education.map((e, i) => (
                                    <div key={i} className="edu-item">
                                        <div className="edu-item__top"><span className="edu-item__school">{e.school}</span><span className="edu-item__year">{e.year}</span></div>
                                        <div className="edu-item__degree">{e.degree}</div>
                                    </div>
                                ))}
                            </div>
                            {data.achievements && data.achievements.length > 0 && (
                                <>
                                    <div className="bio__divider" />
                                    <div className="card-label" style={{ marginTop: 24, marginBottom: 20 }}>achievements</div>
                                    <div className="edu-list">
                                        {data.achievements.map((ach, i) => (
                                            <div key={i} className="edu-item">
                                                <div className="edu-item__top"><span className="edu-item__school">{ach.title}</span><span className="edu-item__year">{ach.year}</span></div>
                                                <div className="edu-item__degree">{ach.role}</div>
                                                <p className="exp-item__desc" style={{ marginTop: 8, fontSize: '12px', opacity: 0.8 }}>{ach.desc}</p>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </Glass>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Projects({ data }) {
    const ref = useRef(null);
    const vis = useInView(ref);
    return (
        <section id="projects" ref={ref} className="section section--projects">
            <div className="section__inner">
                <div className={`fade-up ${vis ? "fade-up--visible" : ""}`}><span className="section__label">02 / projects</span><h2 className="section__heading">Things I've<br /><span className="section__heading-accent section__heading-accent--pink">shipped</span></h2></div>
                <div className="projects__grid">
                    {data.projects.map((p, i) => (
                        <div key={p.id} className={`fade-up ${vis ? "fade-up--visible" : ""}`} style={{ transitionDelay: `${0.08 + i * 0.08}s` }}>
                            <div className="project-card">
                                <div className="project-card__glow" style={{ background: `radial-gradient(circle, ${p.glow} 0%, transparent 70%)` }} />
                                <div className="project-card__top">
                                    <span className="project-card__tag" style={{ background: `${p.accent}14`, color: p.accent, border: `1px solid ${p.accent}28` }}>{p.tag}</span>
                                    <span className="project-card__year">{p.year}</span>
                                </div>
                                <h3 className="project-card__title">{p.title}</h3>
                                <p className="project-card__desc">{p.desc}</p>
                                <a href={p.link || "#"} target="_blank" rel="noopener noreferrer" className="project-card__link" style={{ color: p.accent, textDecoration: 'none' }}>
                                    view project <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 17L17 7M17 7H7M17 7v10" /></svg>
                                </a>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Contact() {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [status, setStatus] = useState({ loading: false, succeeded: false, error: null });

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, succeeded: false, error: null });
        try {
            const response = await fetch("https://formspree.io/f/xdapkewd", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (response.ok) {
                setStatus({ loading: false, succeeded: true, error: null });
                setForm({ name: "", email: "", message: "" });
            } else {
                setStatus({ loading: false, succeeded: false, error: "Submission failed." });
            }
        } catch (err) {
            setStatus({ loading: false, succeeded: false, error: "Something went wrong." });
        }
    };

    return (
        <section id="contact" className="section section--contact">
            <div className="contact__inner">
                <div className="contact__header fade-up fade-up--visible">
                    <span className="section__label">03 / contact</span>
                    <h2 className="section__heading">Let's build<br /><span className="section__heading-accent section__heading-accent--blue">together.</span></h2>
                </div>
                <Glass className="contact-form fade-up fade-up--visible">
                    {status.succeeded ? (
                        <div className="form__success">
                            <div className="form__success-title">Message sent</div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="form__grid">
                                <div className="form__field">
                                    <label className="form__label">Name</label>
                                    <input name="name" type="text" required value={form.name} onChange={handleChange} className="form__input" />
                                </div>
                                <div className="form__field">
                                    <label className="form__label">Email</label>
                                    <input name="email" type="email" required value={form.email} onChange={handleChange} className="form__input" />
                                </div>
                            </div>
                            <div className="form__message-wrap">
                                <label className="form__label">Message</label>
                                <textarea name="message" rows={4} required value={form.message} onChange={handleChange} className="form__textarea" />
                            </div>
                            <button type="submit" className="form__submit" disabled={status.loading}>
                                {status.loading ? "Sending..." : "Send to Email"}
                            </button>
                        </form>
                    )}
                </Glass>
            </div>
        </section>
    );
}

/* ─────────────────────────────────────────
   Interactive Floating Robot (RaktheBot)
 ───────────────────────────────────────── */
function InteractivePet() {
    const mouse = useMousePosition();
    const [bubbleText, setBubbleText] = useState("");
    const [bubbleVisible, setBubbleVisible] = useState(false);
    const bubbleTimeoutRef = useRef(null);


    const messages = [
        // 👋 Bot intro
        "👋 Yo! RaktheBot here. Bhavesh's digital twin... kinda.",
        "🤖 Not ChatGPT, just your friendly neighborhood portfolio bot.",
        "🫡 Welcome to Bhavesh's corner of the internet.",
        "👀 You clicked the portfolio. The lore begins here.",
        "⚡ Warning: excessive curiosity may lead to cool projects.",

        // 💻 Developer vibes
        "💻 I speak fluent JavaScript. Sarcasm is my second language.",
        "☕ Powered by code, caffeine, and questionable debugging hours.",
        "🐛 It's not a bug. It's an unexpected feature. Probably.",
        "🔧 Turning 'what if?' into 'it's live!'",
        "🧠 Big ideas, small commits, occasional existential crises.",
        "🚀 From localhost to the real world. That's the goal.",
        "⌨️ Code, break things, fix things, repeat. The dev life.",
        "✨ Clean UI, smooth UX, zero unnecessary drama.",
        "📦 It worked on my machine. Now let's make it work everywhere.",

        // 🚀 Projects
        "🏛️ Built the PES Central Library portal. Yep, it's the real deal.",
        "👨‍💻 Curious how the library portal works? GitHub has the receipts.",
        "💳 CC Reward Optimizer: making credit card rewards less confusing.",
        "🔥 Real projects > another todo app. You know the vibe.",
        "🛠️ Built from scratch, shipped for real. No tutorial-only projects.",
        "🌐 Some ideas deserve more than just a Figma file.",
        "📱 Small screen, big ideas. Check out the mobile projects.",
        "🧪 Side projects are where the fun stuff happens.",

        // 👁️ Interactive messages
        "👀 Move your mouse. I'm trying to make eye contact.",
        "🫵 Yes, you. Try clicking around. I won't judge.",
        "🖱️ Go on, explore. This portfolio isn't just for decoration.",
        "✨ Plot twist: the portfolio is interactive too.",
        "😏 I saw that hover. Nice move.",
        "🎮 Your mission: find your favorite project.",
        "🔍 Easter eggs? Maybe. You'll have to look around.",
        "👁️ The eyes follow the cursor. The cursor follows the chaos.",

        // 🤝 Career & collaboration
        "🤝 Got an idea? Let's turn it into something real.",
        "📩 Recruiter spotted? Bhavesh is just one email away.",
        "🚀 Have a project in mind? Let's cook something up.",
        "💼 Open to cool opportunities and interesting challenges.",
        "🧑‍💻 Need someone who loves building things? Let's talk.",
        "🌱 Always learning, always building, never done leveling up.",
        "📬 Your next favorite developer might be one message away.",
        "🤝 Good ideas are cool. Building them together is cooler.",

        // 😂 Random Gen Z energy
        "💀 That bug had me questioning my entire career.",
        "🗿 The code is coding. The vibes are vibing.",
        "📈 Character development, but make it technical.",
        "🧃 Running on curiosity and pure delusion. Respectfully.",
        "🫠 CSS said no. I said try again.",
        "🎯 Less yap, more ship.",
        "💅 Making pixels behave since forever. Almost.",
        "🧠 99% curiosity, 1% pretending I know what's happening.",
        "🫡 Main character energy. Developer edition.",
        "⚡ Side quest today, shipped feature tomorrow.",
        "🎨 If it looks clean, the mission is halfway complete.",
        "🌚 The deadline is approaching. Suddenly, I am productive.",
    ];


    const handleClick = () => {
        if (bubbleTimeoutRef.current) clearTimeout(bubbleTimeoutRef.current);
        const randomMsg = messages[Math.floor(Math.random() * messages.length)];
        setBubbleText(randomMsg);
        setBubbleVisible(true);
        bubbleTimeoutRef.current = setTimeout(() => {
            setBubbleVisible(false);
        }, 5000);
    };

    const eyeX = (mouse.x - window.innerWidth / 2) * 0.005;
    const eyeY = (mouse.y - window.innerHeight / 2) * 0.005;

    return (
        <div className="robot-container">
            <AnimatePresence>
                {bubbleVisible && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: 10 }}
                        className="robot-speech"
                    >
                        {bubbleText}
                    </motion.div>
                )}
            </AnimatePresence>
            <div className="robot-avatar" onClick={handleClick}>
                <div className="robot-antenna" />
                <div className="robot-body">
                    <div className="robot-face">
                        <div className="robot-eye" style={{ transform: `translate(${eyeX}px, ${eyeY}px)` }} />
                        <div className="robot-eye" style={{ transform: `translate(${eyeX}px, ${eyeY}px)` }} />
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────
   Main App
 ───────────────────────────────────────── */
export default function App() {
    const scrollY = useScrollY();
    const isMobile = useIsMobile();
    const [data] = useState(initialData);

    useEffect(() => {
        if ('scrollRestoration' in window.history) {
            window.history.scrollRestoration = 'manual';
        }
        window.scrollTo(0, 0);
        const t = setTimeout(() => {
            window.scrollTo(0, 0);
        }, 50);
        return () => clearTimeout(t);
    }, []);

    return (
        <>
            <BackgroundStars />
            <Nav scrollY={scrollY} />
            <Hero isMobile={isMobile} data={data} />
            <About data={data} />
            <Projects data={data} />
            <Contact />
            <MobileNav />
            <footer className="footer">
                <div className="socials">
                    <a href="https://github.com/bhaveshm05" target="_blank" rel="noopener noreferrer" className="socials__link" title="GitHub"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7a3.37 3.37 0 0 0-.94 2.58V22" /></svg></a>
                    <a href="https://linkedin.com/in/bhavesh-m-rakthe-0361aa21b" target="_blank" rel="noopener noreferrer" className="socials__link" title="LinkedIn"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" /></svg></a>
                    <a href="mailto:bhaveshmrakthee@gmail.com" className="socials__link" title="Email"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg></a>
                    <a href="https://www.instagram.com/bhavesh_m_rakthe?igsh=MTZmZXVwaHNkend0cg==" target="_blank" rel="noopener noreferrer" className="socials__link" title="Instagram"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></svg></a>
                </div>
                <span className="footer__text">© 2026 · built by {data.bio.name}</span>
            </footer>
            <InteractivePet />
        </>
    );
}
